/**
 * Return-URL plumbing for the login/signup flow.
 *
 * Anything that bounces a visitor to `/auth/login` must carry where they were
 * going, or they land on the dashboard afterwards and have to find their way
 * back by hand. That is especially bad for shared program join links, where the
 * `?p=<token>` in the URL is the whole point of the visit.
 *
 * The convention: `?returnUrl=<encoded path + query>`, read by
 * `pages/auth/Login.tsx` and `pages/affiliate/auth/SignUp.tsx`.
 */

export const RETURN_URL_PARAM = "returnUrl";

const LOGIN_PATH = "/auth/login";

/** Paths we must never send someone back to after they sign in. */
const NON_RETURNABLE = ["/auth/", "/affiliate/auth/", "/brand/auth/", "/agency/auth/"];

function isReturnable(path: string): boolean {
  // Only same-origin, absolute-path destinations. Anything else (protocol,
  // protocol-relative `//evil.com`) would be an open redirect.
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  return !NON_RETURNABLE.some((prefix) => path.startsWith(prefix));
}

/** `/auth/login?returnUrl=…` for a destination, or plain `/auth/login`. */
export function loginUrlFor(destination: string): string {
  if (!isReturnable(destination)) return LOGIN_PATH;
  return `${LOGIN_PATH}?${RETURN_URL_PARAM}=${encodeURIComponent(destination)}`;
}

/** `/auth/login?returnUrl=<where the browser is right now>`. */
export function loginUrlForCurrentLocation(
  location: { pathname: string; search?: string } = window.location
): string {
  return loginUrlFor(`${location.pathname}${location.search || ""}`);
}

/**
 * Pull a safe `returnUrl` off a query string.
 *
 * Returns undefined for anything that isn't a same-origin path, so a crafted
 * `?returnUrl=https://evil.com` can't turn our login page into a redirector.
 */
export function readReturnUrl(search: string): string | undefined {
  const raw = new URLSearchParams(search).get(RETURN_URL_PARAM);
  if (!raw) return undefined;
  // URLSearchParams already decodes once; callers historically decoded again,
  // so tolerate a double-encoded value rather than breaking old links.
  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch {
    value = raw;
  }
  return isReturnable(value) ? value : undefined;
}

/** Carry an existing returnUrl onto a sibling auth link (sign up, forgot password). */
export function withReturnUrl(path: string, returnUrl?: string): string {
  if (!returnUrl || !isReturnable(returnUrl)) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${RETURN_URL_PARAM}=${encodeURIComponent(
    returnUrl
  )}`;
}
